import { ReactNode } from 'react';

import {
  AnnotateScanProfileConfig,
  ProfileOption,
} from '@/components/actions/types';

import {
  AnnotateActionConfig,
  CustomActionConfig,
  DropdownOption,
  ExportActionConfig,
  GenericCustomActionConfig,
  ImportActionConfig,
  ManageActionConfig,
  NoteActionConfig,
  ScanActionConfig,
  SeverityChangeActionConfig,
  ShareActionConfig,
  ShareVariant,
  StatusChangeActionConfig,
  StatusOption,
} from './types';

/**
 * Build annotate action configuration
 *
 * Creates configuration for note, tag, and remove tag operations.
 *
 * @param config - Annotate configuration
 * @returns Annotate action configuration
 *
 * @example
 * ```tsx
 * buildAnnotateAction({
 *   onCommentClick: () => openNoteModal(),
 *   onTagClick: () => openTagModal(),
 *   onRemoveTagClick: () => openRemoveTagModal(),
 *   labels: { comment: 'Add Note' },
 * })
 * ```
 */
export function buildAnnotateAction(config: {
  onCommentClick: () => void;
  onTagClick: () => void;
  onRemoveTagClick: () => void;
  labels?: Partial<Record<'comment' | 'tag' | 'removeTag', string>>;
  /** Optional scan profile management config */
  scanProfile?: AnnotateScanProfileConfig;
  disabled?: boolean;
  testId?: string;
}): AnnotateActionConfig {
  return {
    type: 'Annotate',
    handlers: {
      onCommentClick: config.onCommentClick,
      onTagClick: config.onTagClick,
      onRemoveTagClick: config.onRemoveTagClick,
    },
    labels: config.labels,
    scanProfile: config.scanProfile,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build scan action configuration (handler-based)
 *
 * Creates configuration for simple handler-based scan patterns
 * used by Seeds and Vulnerabilities sections.
 *
 * @param config - Scan configuration with handlers
 * @returns Scan action configuration
 *
 * @example
 * ```tsx
 * buildScanActionHandlers({
 *   handlers: {
 *     onComprehensiveScan: () => runScan('comprehensive'),
 *     onNetworkScan: () => runScan('network'),
 *   },
 *   disabledActions: { network: !hasValidTargets },
 * })
 * ```
 */
export function buildScanActionHandlers(config: {
  handlers: Partial<{
    onComprehensiveScan: () => void;
    onAffiliationScan: () => void;
    onNetworkScan: () => void;
    onPortScan: () => void;
    onCrawlScan: () => void;
    onWebScan: () => void;
    onRescanClick: () => void;
    onAffiliationClick: () => void;
  }>;
  labels?: Record<string, string>;
  disabledActions?: Record<string, boolean>;
  hideActions?: Record<string, boolean>;
  disabled?: boolean;
  testId?: string;
}): ScanActionConfig {
  return {
    type: 'Scan',
    variant: 'handlers',
    handlers: config.handlers,
    labels: config.labels,
    disabledActions: config.disabledActions,
    hideActions: config.hideActions,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build scan action configuration (options-based)
 *
 * Creates configuration for complex dropdown options pattern
 * used by Assets section with dynamic scan option building.
 *
 * @param config - Scan configuration with dropdown options
 * @returns Scan action configuration
 *
 * @example
 * ```tsx
 * const scanOptions = getAssetScanOptions({
 *   assets,
 *   handlers,
 *   selectUserAgent,
 *   isPraetorianUser,
 * });
 *
 * buildScanActionOptions({
 *   scanOptions,
 * })
 * ```
 */
export function buildScanActionOptions(config: {
  scanOptions: DropdownOption[];
  disabled?: boolean;
  testId?: string;
}): ScanActionConfig {
  return {
    type: 'Scan',
    variant: 'options',
    scanOptions: config.scanOptions,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build share action configuration
 *
 * Creates configuration for share and export operations with different UI variants.
 *
 * @param config - Share configuration
 * @returns Share action configuration
 *
 * @example
 * ```tsx
 * // Dropdown variant (Seeds)
 * buildShareAction({
 *   variant: 'dropdown',
 *   onExportClick: () => openExportModal(),
 *   onShareClick: () => copyShareURLs(),
 *   labels: { export: 'Export Seeds' },
 * })
 *
 * // Button only variant (Assets/Vulnerabilities)
 * buildShareAction({
 *   variant: 'button-only',
 *   onShareClick: () => handleShare(),
 * })
 * ```
 */
export function buildShareAction(config: {
  variant: ShareVariant;
  onExportClick?: () => void;
  onShareClick?: () => void;
  labels?: {
    export?: string;
    share?: string;
  };
  disabled?: boolean;
  testId?: string;
}): ShareActionConfig {
  return {
    type: 'Share',
    variant: config.variant,
    onExportClick: config.onExportClick,
    onShareClick: config.onShareClick,
    labels: config.labels,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build export action configuration
 *
 * Creates configuration for standalone export button (always enabled, right-aligned).
 *
 * @param config - Export configuration
 * @returns Export action configuration
 *
 * @example
 * ```tsx
 * buildExportAction({
 *   onExportClick: () => openExportModal(),
 * })
 * ```
 */
export function buildExportAction(config: {
  onExportClick: () => void;
  disabled?: boolean;
  alwaysEnabled?: boolean;
  testId?: string;
}): ExportActionConfig {
  return {
    type: 'Export',
    onExportClick: config.onExportClick,
    disabled: config.disabled,
    alwaysEnabled: config.alwaysEnabled ?? true, // Default to always enabled
    testId: config.testId,
  };
}

/**
 * Build import action configuration
 *
 * Creates configuration for standalone import button (always enabled).
 *
 * @param config - Import configuration
 * @returns Import action configuration
 *
 * @example
 * ```tsx
 * buildImportAction({
 *   onImportClick: () => openImportModal(),
 * })
 * ```
 */
export function buildImportAction(config: {
  onImportClick: () => void;
  disabled?: boolean;
  alwaysEnabled?: boolean;
  testId?: string;
}): ImportActionConfig {
  return {
    type: 'Import',
    onImportClick: config.onImportClick,
    disabled: config.disabled,
    alwaysEnabled: config.alwaysEnabled ?? true, // Default to always enabled
    testId: config.testId,
  };
}

/**
 * Build manage action configuration
 *
 * Creates configuration for freeze, activate, delete, and custom status changes.
 *
 * @param config - Manage configuration
 * @returns Manage action configuration
 *
 * @example
 * ```tsx
 * // Simple manage (Assets)
 * buildManageAction({
 *   onFreezeClick: () => updateStatus('frozen'),
 *   onActivateClick: () => updateStatus('active'),
 *   onDeleteClick: () => updateStatus('deleted'),
 *   shortcuts: { freeze: '⌥ F', delete: '⌥ D' },
 * })
 *
 * // With custom status options (Seeds)
 * buildManageAction({
 *   onFreezeClick: () => updateStatus('freeze'),
 *   onActivateClick: () => updateStatus('accept'),
 *   onDeleteClick: () => updateStatus('delete'),
 *   statusOptions: [
 *     { label: 'Approve', value: 'accept', icon: <CheckCircle />, shortcut: '⌥ A' },
 *     { label: 'Reject', value: 'reject', icon: <XCircle />, shortcut: '⌥ R' },
 *   ],
 *   onStatusChangeClick: (status) => updateStatus(status),
 *   labels: { activate: 'Unfreeze' },
 * })
 * ```
 */
export function buildManageAction(config: {
  onFreezeClick: () => void;
  onActivateClick: () => void;
  onDeleteClick: () => void;
  statusOptions?: StatusOption[];
  onStatusChangeClick?: (status: string) => void;
  profileOptions?: ProfileOption[];
  onProfileChangeClick?: (profileId: string) => void;
  currentProfileId?: string;
  labels?: Partial<Record<'freeze' | 'activate' | 'delete', string>>;
  shortcuts?: Partial<Record<'freeze' | 'activate' | 'delete', string>>;
  hideActions?: Partial<
    Record<'freeze' | 'activate' | 'delete' | 'statusChange', boolean>
  >;
  disabled?: boolean;
  testId?: string;
}): ManageActionConfig {
  return {
    type: 'Manage',
    handlers: {
      onFreezeClick: config.onFreezeClick,
      onActivateClick: config.onActivateClick,
      onDeleteClick: config.onDeleteClick,
      onStatusChangeClick: config.onStatusChangeClick,
      onProfileChangeClick: config.onProfileChangeClick,
    },
    statusOptions: config.statusOptions,
    profileOptions: config.profileOptions,
    currentProfileId: config.currentProfileId,
    labels: config.labels,
    shortcuts: config.shortcuts,
    hideActions: config.hideActions,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build status change action configuration
 *
 * Creates configuration for vulnerability status changes (Triage, Open, Closed, etc.).
 * Includes modal for "Reject" status with close reason selection.
 *
 * @template T - Type of risk items (must have key and status)
 * @param config - Status change configuration
 * @returns Status change action configuration
 *
 * @example
 * ```tsx
 * buildStatusChangeAction({
 *   risks: selectedVulnerabilities,
 *   onUpdateRisk: async (risk, comment) => {
 *     await handleUpdateRisk(risk);
 *     clearSelection();
 *   },
 *   getRiskStatusLabel: (status) => parseRiskStatus(status),
 * })
 * ```
 */
export function buildStatusChangeAction<
  T extends { key: string; status: string },
>(config: {
  risks: T[];
  onUpdateRisk: (risk: T, comment?: string) => Promise<void>;
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  disabled?: boolean;
  hideLabel?: boolean;
  testId?: string;
}): StatusChangeActionConfig<T> {
  return {
    type: 'Change Status',
    risks: config.risks,
    handlers: {
      onUpdateRisk: config.onUpdateRisk,
    },
    getRiskStatusLabel: config.getRiskStatusLabel,
    disabled: config.disabled,
    hideLabel: config.hideLabel,
    testId: config.testId,
  };
}

/**
 * Build severity change action configuration
 *
 * Creates configuration for vulnerability severity changes (Critical, High, Medium, Low).
 * Changes apply immediately without modal confirmation.
 *
 * @template T - Type of risk items (must have key and status)
 * @param config - Severity change configuration
 * @returns Severity change action configuration
 *
 * @example
 * ```tsx
 * buildSeverityChangeAction({
 *   risks: selectedVulnerabilities,
 *   severityOptions: severityFilterOptions,
 *   onSeverityChange: (option) => {
 *     updateSeverity(option.value);
 *     clearSelection();
 *   },
 * })
 * ```
 */
export function buildSeverityChangeAction<
  T extends { key: string; status: string },
>(config: {
  risks: T[];
  severityOptions: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  onSeverityChange: (option: {
    value?: string | number;
    label: string;
    severity?: string;
  }) => void;
  disabled?: boolean;
  hideLabel?: boolean;
  testId?: string;
}): SeverityChangeActionConfig<T> {
  return {
    type: 'Change Severity',
    risks: config.risks,
    severityOptions: config.severityOptions,
    handlers: {
      onSeverityChange: config.onSeverityChange,
    },
    disabled: config.disabled,
    hideLabel: config.hideLabel,
    testId: config.testId,
  };
}

/**
 * Build custom action slot
 *
 * @deprecated Use buildStatusChangeAction or buildSeverityChangeAction instead
 *
 * Creates configuration for section-specific actions with custom rendering.
 * Used for unique needs like Vulnerability Change Status and Change Severity.
 *
 * @param type - Action type
 * @param render - Function that returns the action component
 * @returns Custom action configuration
 *
 * @example
 * ```tsx
 * buildCustomAction('Change Status', () => (
 *   <ChangeRiskStatus
 *     risks={vulnerabilities}
 *     onUpdateRisk={onUpdateRisk}
 *   />
 * ))
 * ```
 */
export function buildCustomAction(
  type: 'Change Status' | 'Change Severity',
  render: () => ReactNode,
  config?: {
    disabled?: boolean;
    testId?: string;
  }
): CustomActionConfig {
  return {
    type,
    render,
    disabled: config?.disabled,
    testId: config?.testId,
  };
}

/**
 * Build note action configuration
 *
 * Creates configuration for a standalone "Add Note" button.
 * Use this when you want a dedicated note button visible in the action bar,
 * instead of having "Add Note" buried inside the Annotate dropdown.
 *
 * @param config - Note configuration
 * @returns Note action configuration
 *
 * @example
 * ```tsx
 * buildNoteAction({
 *   onNoteClick: () => openNoteModal(),
 *   label: 'Add Note',
 * })
 * ```
 */
export function buildNoteAction(config: {
  onNoteClick: () => void;
  label?: string;
  disabled?: boolean;
  testId?: string;
}): NoteActionConfig {
  return {
    type: 'Note',
    onNoteClick: config.onNoteClick,
    label: config.label,
    disabled: config.disabled,
    testId: config.testId,
  };
}

/**
 * Build generic custom action configuration
 *
 * Creates configuration for rendering arbitrary React components in the action bar.
 * Use this for section-specific buttons or dropdowns that don't fit standard action patterns.
 *
 * @param config - Custom action configuration
 * @returns Generic custom action configuration
 *
 * @example
 * ```tsx
 * // Simple button
 * buildGenericCustomAction({
 *   render: () => (
 *     <Button
 *       size="xs"
 *       onClick={handleAuxInfo}
 *       label="Add Aux Info"
 *     />
 *   ),
 * })
 *
 * // Primary action button (first position)
 * buildGenericCustomAction({
 *   render: () => (
 *     <Button
 *       styleType="primary"
 *       onClick={handleAdd}
 *       label="Add Item"
 *     />
 *   ),
 *   position: 'first', // Render before standard actions
 * })
 * ```
 */
export function buildGenericCustomAction(config: {
  render: () => ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  testId?: string;
  /** Position in action bar: 'first' renders before standard actions */
  position?: 'first' | 'default';
}): GenericCustomActionConfig {
  return {
    type: 'Custom',
    render: config.render,
    disabled: config.disabled,
    hidden: config.hidden,
    testId: config.testId,
    position: config.position,
  };
}
